import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import DefaultEditor from "react-simple-wysiwyg";
import { apiRequestHandler } from "../../apiConfig/service";
import { toast } from "react-toastify";
import Select from "react-select";

const validationSchema = Yup.object({
  jobTitle: Yup.string().required("Job title is required"),
  minExp: Yup.string().required("Minimum Experience is required"),
  maxExp: Yup.string().required("Maximum Experience is required"),
  jobType: Yup.string().required("Job Type is required"),
  department: Yup.object().required("Department is required"),
  noOfVacancies: Yup.string().required("Number of vacancies is required"),
  jobShortDescription: Yup.string().required("Job short description is required"),
  jobLongDescription: Yup.string().required("Job long description is required"),
});

const styles = {
  formContainer: {
    minHeight: "100vh",
    padding: "20px 20px 0px 20px",
    background: "#f5f7fa",
  },
  card: {
    borderRadius: "10px",
    padding: "0px 15px 15px 15px",
  },
  label: {
    fontWeight: "bold",
    fontSize: "14px",
    color: "#484848",
    display: "block",
    marginBottom: "0.25rem",
  },
  input: {
    width: "100%",
    padding: "0.5rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    outline: "none",
  },
  error: {
    color: "#e74c3c",
    fontSize: "12px",
    marginTop: "4px",
  },
  backBtn: {
    padding: "6px 12px",
    background: "linear-gradient(to bottom right, #163252, #0081ff)",
    color: "#fff",
    border: "1px solid #ccc",
    borderRadius: "6px",
    cursor: "pointer",
  },
  submitBtn: {
    padding: "10px 20px",
    background: "linear-gradient(to bottom right, #163252, #0081ff)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer",
    width: "100%",
  },
  removeBtn: {
    padding: "6px 12px",
    backgroundColor: "#e74c3c",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
};

const AddJobForm = () => {
  const { state } = useLocation();
  const job = state?.job;
  const mode = state?.mode || "add";
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [titles, setTitles] = useState([""]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  useEffect(() => {
    const fetchDepartments = async () => {
      const res = await apiRequestHandler({
        endPoint: "allDepartment",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const formatted = res?.department?.map(dep => ({ value: dep._id, label: dep.departmentName })) || [];
      setDepartmentOptions(formatted);

      if (job?.department) {
        const matched = formatted.find(dep => dep.value === job.department);
        if (matched) setSelectedDepartment(matched);
      }
    };
    fetchDepartments();

    if (job?.jdFile?.length > 0) {
      setFiles(Array(job.jdFile.length).fill(null));
      setTitles(job.jdFile.map(f => f.title));
    }
  }, [job]);

  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const newFiles = [...files];
      newFiles[index] = file;
      setFiles(newFiles);
    }
  };

  const handleTitleChange = (e, index) => {
    const newTitles = [...titles];
    newTitles[index] = e.target.value;
    setTitles(newTitles);
  };

  const addFileField = () => {
    setFiles([...files, null]);
    setTitles([...titles, ""]);
  };

  const removeFileField = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedTitles = titles.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setTitles(updatedTitles);
  };

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("jobTitle", values.jobTitle);
      formData.append("minExp", values.minExp);
      formData.append("maxExp", values.maxExp);
      formData.append("jobType", values.jobType);
      formData.append("department", values.department.value);
      formData.append("noOfVacancies", values.noOfVacancies);
      formData.append("jobShortDescription", values.jobShortDescription);
      formData.append("jobLongDescription", values.jobLongDescription);
      if (job?._id) formData.append("_id", job._id);

      files.forEach((file, i) => {
        if (file) {
          formData.append("documents", file);
          formData.append("title", titles[i]);
        }
      });

      const endpoint = isEditMode ? "updateJob" : "addJob";
      const res = await apiRequestHandler({
        method: "POST",
        endPoint: endpoint,
        data: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (res?.success) {
        toast.success(res.message);
        navigate("/admin/job-list");
        resetForm();
      } else {
        toast.error(res.message || "Something went wrong.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.formContainer}>
      <div style={styles.card}>
        <Formik
          enableReinitialize
          initialValues={{
            jobTitle: job?.jobTitle || "",
            minExp: job?.minExp || "",
            maxExp: job?.maxExp || "",
            jobType: job?.jobType || "",
            department: selectedDepartment || null,
            noOfVacancies: job?.noOfVacancies || "",
            jobShortDescription: job?.jobShortDescription || "",
            jobLongDescription: job?.jobLongDescription || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form className="row g-4">
              {/* Section 1: Header */}
              <div className="col-12 bg-white custom-rounded">
                <div className="d-flex justify-content-between flex-row-reverse align-items-center p-3">
                  <div onClick={() => navigate(-1)} style={styles.backBtn}>← Back</div>
                  <h4 style={{ marginBottom: "0px" }}>
                    {isViewMode ? "View Job" : isEditMode ? "Edit Job" : "Add New Job"}
                  </h4>
                </div>
              </div>

              {/* Section 2: Job Details */}
              <h5 className="custom-head">Job Details</h5>
              <div className="col-12 bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  {["jobTitle", "minExp", "maxExp", "jobType", "noOfVacancies"].map((field, i) => (
                    <div className="col-md-6" key={i}>
                      <label style={styles.label}>
                        {field.replace(/([A-Z])/g, " $1").trim().replace(/^./, str => str.toUpperCase())}
                      </label>

                      <Field name={field} style={styles.input} placeholder={`Enter ${field.replace(/([A-Z])/g, " $1").toLowerCase()}`} readOnly={isViewMode} />
                      <ErrorMessage name={field} component="div" style={styles.error} />
                    </div>
                  ))}
                  <div className="col-md-6">
                    <label style={styles.label}>Department</label>
                    <Select
                      name="department"
                      options={departmentOptions}
                      value={values.department}
                      onChange={(val) => setFieldValue("department", val)}
                      isDisabled={isViewMode}
                    />
                    <ErrorMessage name="department" component="div" style={styles.error} />
                  </div>
                </div>
              </div>

              {/* Section 3: Upload Documents */}
              <h5 className="custom-head">Upload Documents</h5>
              <div className="col-12 bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  {titles.map((title, index) => (
                    <div className="row g-2" key={index}>
                      <div className="col-md-5">
                        <label style={styles.label}>Document</label>
                        <input
                          type="file"
                          onChange={(e) => handleFileChange(e, index)}
                          disabled={isViewMode}
                          style={styles.input}
                        />
                      </div>
                      <div className="col-md-5">
                        <label style={styles.label}>Document Title</label>
                        <input
                          type="text"
                          placeholder="Enter title"
                          value={title}
                          onChange={(e) => handleTitleChange(e, index)}
                          disabled={isViewMode}
                          style={styles.input}
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        {!isViewMode && (
                          <button
                            type="button"
                            onClick={() => removeFileField(index)}
                            style={styles.removeBtn}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {!isViewMode && (
                    <div className="col-2">
                      <button type="button" onClick={addFileField} style={styles.submitBtn}>
                        Add More File
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Section 4: Descriptions */}
              <h5 className="custom-head">Descriptions</h5>
              <div className="col-12 bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  {["jobShortDescription", "jobLongDescription"].map((desc, i) => (
                    <div className="col-12" key={i}>
                      <label style={styles.label}>{desc.replace(/([A-Z])/g, " $1").trim().replace(/^./, str => str.toUpperCase())}</label>
                      <DefaultEditor
                        value={values[desc]}
                        onChange={(e) => setFieldValue(desc, e.target.value)}
                        disabled={isViewMode}
                      />
                      <ErrorMessage name={desc} component="div" style={styles.error} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              {!isViewMode && (
                <div className="col-12 text-center">
                  <button type="submit" style={styles.submitBtn} disabled={loading}>
                    {loading ? "Submitting..." : isEditMode ? "Update Job" : "Create Job"}
                  </button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddJobForm;